import { useCallback, useRef } from 'react'

import { newUuid } from '@krp/client-runtime'
import { CATALOG_SOURCE, shownError, syncSummary, type ShownError, type SourceTile } from '@krp/sync-ui'

/** Что рисует окно по результату запроса: загрузка, отказ (текст службы дословно) или данные. */
export interface QueryState {
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  refreshing: boolean
}

interface QueryLike<T> {
  data?: T | undefined
  error?: unknown
  isError: boolean
  isFetching: boolean
}

export function queryState<T>(result: QueryLike<T>, title: string): QueryState {
  if (result.isError) return { status: 'failed', loadError: shownError(title, result.error), refreshing: false }
  if (result.data === undefined) return { status: 'loading', loadError: null, refreshing: false }
  return { status: 'ready', loadError: null, refreshing: result.isFetching }
}

/**
 * Ключ операции — на намерение человека: повтор после обрыва ответа несёт тот же ключ, и служба вернёт исполненный итог, а не
 * выполнит команду второй раз (ADR-11). Подтверждённое намерение ключ отпускает: следующее такое же нажатие — новая команда.
 */
export function useOperationIds(): { operationFor: (intent: string) => string; settled: (intent: string) => void } {
  const operations = useRef(new Map<string, string>())
  const operationFor = useCallback((intent: string): string => {
    const known = operations.current.get(intent)
    if (known !== undefined) return known
    const created = newUuid()
    operations.current.set(intent, created)
    return created
  }, [])
  const settled = useCallback((intent: string) => {
    operations.current.delete(intent)
  }, [])
  return { operationFor, settled }
}

export interface SyncSources {
  sources: SourceTile[]
  nameOf: (resourceId: string) => string
}

/**
 * Источники из сводки синхронизации: служба отдаёт только те, к которым человек допущен. Имя — для подписей значений и целей. Общий
 * каталог называется словами — и окно общего каталога, и его идентификатор стороны в строках фото (RP-SYN7).
 */
export function useSyncSources(): SyncSources {
  const summary = syncSummary.useQuery()
  const sources = summary.data?.sources ?? []
  const common = summary.data?.commonCatalog.resourceId
  const nameOf = (resourceId: string): string =>
    resourceId === CATALOG_SOURCE || resourceId === common
      ? 'Общий каталог'
      : (sources.find((source) => source.resourceId === resourceId)?.displayName ?? resourceId)
  return { sources, nameOf }
}
